<template>
  <div>
    <div
      v-if="shouldShowRegeneratePromptBlock"
      class="ia-prompt-regenerate mb-3"
    >
      <div class="ia-prompt-regenerate__body">
        <div class="ia-prompt-regenerate__icon">
          <i class="bi bi-arrow-clockwise" />
        </div>

        <div class="ia-prompt-regenerate__content">
          <div class="ia-prompt-regenerate__title">
            This Images report was generated with a different prompt
          </div>

          <div class="ia-prompt-regenerate__text">
            Current report prompt:
            <strong>{{ currentReportPromptLabel }}</strong>.
            Selected prompt:
            <strong>{{ selectedPromptLabel }}</strong>.
          </div>
        </div>
      </div>

      <button
        type="button"
        class="btn btn-success btn-sm ia-prompt-regenerate__btn"
        :disabled="isRegenerating"
        @click="$emit('regenerate')"
      >
        <i
          class="bi"
          :class="isRegenerating ? 'bi-hourglass-split' : 'bi-arrow-clockwise'"
        />
        {{ isRegenerating ? 'Regenerating...' : 'Re-generate with selected prompt' }}
      </button>
    </div>
    <div class="ia-card">
      <!-- Header -->
      <div class="ia-card__header">
        <i
          class="bi bi-image ia-card__icon"
          aria-hidden="true"
        />
        <div class="ia-card__title">
          Image Analysis Overview
        </div>
      </div>

      <!-- Stats row -->
      <div class="ia-stats">
        <!-- Average Score -->
        <div class="ia-stat">
          <div class="ia-stat__label">
            Average Score
          </div>

          <div class="ia-score">
            <div
              class="ia-score__value"
              :class="scoreColorClass"
            >
              {{ averageScore }}
            </div>
            <div class="ia-score__suffix">
              /100
            </div>
          </div>

          <div
            class="ia-progress"
            role="progressbar"
            :aria-valuenow="averageScore"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="ia-progress__bar"
              :style="{ width: `${clampedScore}%` }"
            />
          </div>
        </div>

        <!-- Total Images -->
        <div class="ia-stat">
          <div class="ia-stat__label">
            Total Images
          </div>
          <div class="ia-stat__big">
            {{ images.length }}
          </div>
          <div class="ia-stat__sub" />
        </div>

        <!-- Issues Found -->
        <div class="ia-stat">
          <div class="ia-stat__label">
            Issues Found
          </div>
          <div class="ia-stat__big ia-stat__big--warn">
            {{ imageIssuesFound }}
          </div>
          <div class="ia-stat__sub">
            images need attention
          </div>
        </div>
      </div>
    </div>
    <section class="pi-card mt-4">
      <!-- Header -->
      <div class="pi-head">
        <h2 class="pi-head__title">
          Product Images
        </h2>
        <ImageSequenceDropdown v-if="!readOnly" />
      </div>

      <div 
        class="image-sequence-table-wrap" 
        v-html="preparedImageContent" 
      />
    </section>
  </div>
</template>

<script>
import ImageSequenceDropdown from "./ImageSequenceDropdown.vue";

export default {
  name: "ImageAnalysisOverview",
  components: {
    ImageSequenceDropdown,
  },
  props: {
    pdpReport: {
      type: Object,
      required: true,
    },

    images: {
      type: Array,
      default: () => [],
    },

    analysisContent: {
      type: [Object, String],
      default: () => ({}),
    },

    reportContent: {
      type: Object,
      default: null,
    },

    selectedPromptValue: {
      type: [String, Number, null],
      default: 'default',
    },

    selectedPromptLabel: {
      type: String,
      default: 'Default Prompt',
    },

    isRegenerating: {
      type: Boolean,
      default: false,
    },

    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scoring: false
    }
  },
  computed: {
    normalizedSelectedPromptValue() {
      if (
        this.selectedPromptValue === null ||
        this.selectedPromptValue === undefined ||
        this.selectedPromptValue === ''
      ) {
        return 'default';
      }

      return String(this.selectedPromptValue);
    },

    currentReportPromptValue() {
      if (!this.reportContent) {
        return null;
      }

      if (
        this.reportContent.custom_prompt_id === null ||
        this.reportContent.custom_prompt_id === undefined ||
        this.reportContent.custom_prompt_id === ''
      ) {
        return 'default';
      }

      return String(this.reportContent.custom_prompt_id);
    },

    currentReportPromptLabel() {
      if (!this.reportContent) {
        return 'No report generated';
      }

      if (this.currentReportPromptValue === 'default') {
        return 'Default Prompt';
      }

      return `Custom Prompt #${this.currentReportPromptValue}`;
    },

    shouldShowRegeneratePromptBlock() {
      if (!this.pdpReport || this.readOnly) {
        return false;
      }

      if (!this.reportContent) {
        return true;
      }

      return this.currentReportPromptValue !== this.normalizedSelectedPromptValue;
    },

    normalizedAnalysisObject() {
      if (
        this.analysisContent &&
        typeof this.analysisContent === 'object' &&
        !Array.isArray(this.analysisContent)
      ) {
        return this.analysisContent;
      }

      return {};
    },

    imageStats() {
      return this.normalizedAnalysisObject?.image_stat
        || this.normalizedAnalysisObject?.stats
        || this.pdpReport?.image_stat
        || {};
    },

    imageContent() {
      if (typeof this.analysisContent === 'string') {
        return this.analysisContent;
      }

      return this.normalizedAnalysisObject?.image_content
        || this.normalizedAnalysisObject?.content
        || this.pdpReport?.image_content
        || '';
    },

    preparedImageContent() {
      return this.prepareImages(this.imageContent);
    },

    imageIssuesFound() {
      return this.imageStats?.issues_found ?? 0;
    },
    averageScore() {
      return Math.round(
        this.imageStats?.total_score
        ?? this.normalizedAnalysisObject?.score
        ?? 0
      );
    },
    clampedScore() {
      const n = Number(this.averageScore);
      if (Number.isNaN(n)) return 0;
      return Math.max(0, Math.min(100, n));
    },
    scoreColorClass() {
      const s = this.clampedScore;
      if (s >= 75) return "is-good";
      if (s >= 55) return "is-mid";
      return "is-bad";
    },
    normalizedImages() {
      return (this.images || []).map((x, idx) => {
        const id = x.id ?? `${idx}`;
        const src = x.src || x.url || "";
        const status = String(x.status || "").toLowerCase(); // 'pending'
        const score = typeof x.score === "number" ? x.score : null;

        const isPending = status === "pending" || x.pending === true;
        const isPrimary = !!x.isPrimary;

        let badgeText = null;
        let badgeClass = "";

        if (isPending) {
          badgeText = "Pending";
          badgeClass = "is-pending";
        } else if (score !== null) {
          badgeText = String(score);
          badgeClass = this.scoreToClass(score);
        }

        return {
          ...x,
          id,
          src,
          status,
          score,
          isPrimary,
          badgeText,
          badgeClass,
        };
      });
    },
    analyzedImages() {
      if (!Array.isArray(this.images) || !this.images.length) return 0;

      return this.images.filter(img =>
        (img.status !== 'pending') &&
        typeof img.score === 'number' &&
        !Number.isNaN(img.score)
      ).length;
    },

    pendingAnalysis() {
      if (!Array.isArray(this.images) || !this.images.length) return 0;

      return this.images.filter(img => {
        const status = String(img.status || '').toLowerCase();
        return status === 'pending' || img.pending === true;
      }).length;
    },

    issuesFound() {
      if (!Array.isArray(this.images) || !this.images.length) return 0;

      return this.images.filter(img => {
        if (img.is_issue === true) return true;
        if (img.needs_attention === true) return true;

        if (Array.isArray(img.issues) && img.issues.length) return true;

        if (typeof img.issues_count === 'number' && img.issues_count > 0) return true;

        return false;
      }).length;
    },
  },
  methods: {
    prepareImages(html) {
      if (!html || !Array.isArray(this.images) || !this.images.length) {
        return html;
      }

      return html.replace(/image_(\d+)/g, (match, indexStr) => {
        const index = parseInt(indexStr, 10) - 1;
        const image = this.images[index];

        if (!image) {
          return match;
        }

        let src = '';
        let alt = `image_${index + 1}`;

        if (typeof image === 'string') {
          src = image;
        } else if (image && typeof image === 'object') {
          src = image.src || '';
          alt = image.alt || alt;
        }

        if (!src) {
          return match;
        }

        return `<img width="150" height="150" style="object-fit: contain;" src="${this.escapeHtml(src)}&is_lightweight&scale_width=300" alt="${this.escapeHtml(alt)}" />`;
      });
    },
    escapeHtml(s) {
      return String(s)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
    },
    scoreToClass(score) {
      if (score >= 85) return "is-good";      // green
      if (score >= 70) return "is-mid";       // amber
      return "is-low";                        // orange/red-ish
    },
    runScoreAnalysis() {
      axios.get(`/pdp-analysis/score-images/${this.pdpReport.id}`);
      this.scoring = true;
      this.$bvToast.toast(`Image scoring job running!`, {
        title: 'Success',
        variant: 'success',
      })
    }
  }
};
</script>

<style lang="scss" scoped>
.ia-card {
  background: #fff;
  border: 1px solid #e9eef5;
  border-radius: 14px;
  padding: 22px 22px 18px;
  box-shadow: 0 1px 0 rgba(16, 24, 40, 0.02);

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
  }

  &__icon {
    font-size: 20px;
    color: #111827;
  }

  &__title {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #111827;
  }
}

.ia-stats {
  display: grid;
  grid-template-columns: 1.35fr 1fr 1fr 1fr;
  gap: 34px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
    gap: 22px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.ia-stat {
  min-width: 0;

  &__label {
    font-size: 16px;
    font-weight: 600;
    color: #6b7280; // slate-ish
    margin-bottom: 10px;
  }

  &__big {
    font-size: 44px;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.02em;
    margin-top: 2px;

    &--warn {
      color: #b7791f; // amber-ish
    }
  }

  &__sub {
    margin-top: 10px;
    font-size: 16px;
    font-weight: 600;
    color: #6b7280;
  }
}

.ia-score {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;

  &__value {
    font-size: 54px;
    line-height: 1;
    font-weight: 800;
    letter-spacing: -0.03em;

    &.is-good { color: #22a06b; }
    &.is-mid { color: #b7791f; }
    &.is-bad { color: #dc2626; }
  }

  &__suffix {
    font-size: 20px;
    font-weight: 700;
    color: #6b7280;
  }
}

.ia-progress {
  height: 10px;
  width: 100%;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;

  &__bar {
    height: 100%;
    border-radius: 999px;
    background: #2bb07f;
    width: 0%;
    transition: width 250ms ease;
  }
}

.ia-btn {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f9fafb;
  }

  &:active {
    transform: translateY(1px);
  }

  &__icon {
    font-size: 18px;
    line-height: 1;
  }
}
.pi-card {
  background: #fff;
  border: 1px solid #e9eef5;
  border-radius: 16px;
  padding: 18px 18px 22px;
}

.pi-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;

  &__title {
    margin: 0;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0f172a;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #0f172a;
    font-weight: 700;
    cursor: pointer;

    &:hover { background: #f9fafb; }
    &:active { transform: translateY(1px); }
  }

  &__btnIcon {
    font-size: 18px;
    line-height: 1;
  }
}

.pi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
}

.pi-item {
  position: relative;
  border-radius: 14px;
  border: 1px solid #eef2f7;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 180ms ease, transform 180ms ease;

  &:hover {
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
  }
}

.pi-media {
  aspect-ratio: 1 / 1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
}

.pi-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  min-width: 44px;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
  color: #fff;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12);

  &.is-good { background: #2f9e5b; }
  &.is-mid { background: #d4a62a; }
  &.is-low { background: #d9730d; }
  &.is-pending {
    background: #eef2f7;
    color: #6b7280;
    box-shadow: none;
    font-weight: 700;
    font-size: 16px;
    min-width: 84px;
  }
}

.pi-pill {
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 2;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.95);
  color: #111827;
  font-weight: 700;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
}

.image-sequence-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #ffffff;
}

.image-sequence-table-wrap :deep(table) {
  width: 100%;
  min-width: 1200px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #334155;
  background: #ffffff;
}

.image-sequence-table-wrap :deep(thead th) {
  padding: 16px 18px;
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  color: #334155;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  white-space: normal;
}

.image-sequence-table-wrap :deep(thead th:first-child) {
  border-top-left-radius: 20px;
}

.image-sequence-table-wrap :deep(thead th:last-child) {
  border-top-right-radius: 20px;
}

.image-sequence-table-wrap :deep(tbody td) {
  padding: 18px;
  vertical-align: top;
  color: #475569;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
}

.image-sequence-table-wrap :deep(tbody tr:nth-child(even) td) {
  background: #fcfdff;
}

.image-sequence-table-wrap :deep(tbody tr:hover td) {
  background: #f8fbff;
}

.image-sequence-table-wrap :deep(tbody tr:last-child td:first-child) {
  border-bottom-left-radius: 20px;
}

.image-sequence-table-wrap :deep(tbody tr:last-child td:last-child) {
  border-bottom-right-radius: 20px;
}

.image-sequence-table-wrap :deep(th:nth-child(1)),
.image-sequence-table-wrap :deep(td:nth-child(1)) {
  width: 72px;
  min-width: 72px;
  text-align: center;
  font-weight: 700;
  color: #1e293b;
}

.image-sequence-table-wrap :deep(th:nth-child(2)),
.image-sequence-table-wrap :deep(td:nth-child(2)),
.image-sequence-table-wrap :deep(th:nth-child(3)),
.image-sequence-table-wrap :deep(td:nth-child(3)) {
  width: 150px;
  min-width: 150px;
}

.image-sequence-table-wrap :deep(th:nth-child(4)),
.image-sequence-table-wrap :deep(td:nth-child(4)) {
  width: 140px;
  min-width: 140px;
}

.image-sequence-table-wrap :deep(th:nth-child(5)),
.image-sequence-table-wrap :deep(td:nth-child(5)) {
  width: 32%;
  min-width: 360px;
}

.image-sequence-table-wrap :deep(th:nth-child(6)),
.image-sequence-table-wrap :deep(td:nth-child(6)) {
  width: 36%;
  min-width: 420px;
}

.image-sequence-table-wrap :deep(td:nth-child(2)),
.image-sequence-table-wrap :deep(td:nth-child(3)) {
  font-weight: 600;
  color: #334155;
  word-break: break-word;
}

.image-sequence-table-wrap :deep(td:nth-child(3) span) {
  display: inline-block;
  margin-top: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: #0f766e;
  background: #ecfeff;
  border: 1px solid #bae6fd;
}

.image-sequence-table-wrap :deep(span.success),
.image-sequence-table-wrap :deep(span.warning) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  border: 2px solid transparent;
  background: #fff;
}

.image-sequence-table-wrap :deep(span.success) {
  color: #0a8a18;
  border-color: #0a8a18;
}

.image-sequence-table-wrap :deep(span.warning) {
  color: #ff4d00;
  border-color: #ff4d00;
}

.image-sequence-table-wrap :deep(b) {
  display: inline-block;
  margin-bottom: 6px;
  font-weight: 700;
  color: #1e293b;
}

.image-sequence-table-wrap :deep(ul) {
  margin: 8px 0 0 0;
  padding-left: 18px;
}

.image-sequence-table-wrap :deep(li) {
  margin: 6px 0;
  color: #475569;
}

@media (max-width: 1024px) {
  .image-sequence-table-wrap :deep(table) {
    min-width: 1050px;
    font-size: 13px;
  }

  .image-sequence-table-wrap :deep(thead th),
  .image-sequence-table-wrap :deep(tbody td) {
    padding: 14px;
  }
}
.ia-prompt-regenerate {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: #fffbeb;
}

.ia-prompt-regenerate__body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.ia-prompt-regenerate__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #fef3c7;
  color: #b45309;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ia-prompt-regenerate__content {
  min-width: 0;
}

.ia-prompt-regenerate__title {
  font-size: 14px;
  line-height: 1.3;
  font-weight: 800;
  color: #92400e;
  margin-bottom: 3px;
}

.ia-prompt-regenerate__text {
  font-size: 13px;
  line-height: 1.35;
  color: #78350f;
}

.ia-prompt-regenerate__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.ia-prompt-regenerate__btn i::before {
  vertical-align: top;
}

@media (max-width: 768px) {
  .ia-prompt-regenerate {
    align-items: stretch;
    flex-direction: column;
  }

  .ia-prompt-regenerate__btn {
    justify-content: center;
  }
}
</style>
